import { watch, type Ref, type WatchStopHandle } from 'vue';
import { matchLocale } from './locale';
import { activeLocale, setLocale } from './state';

/**
 * Keeps the Active locale and another locale ref (vue-i18n's `i18n.global.locale`) equal while both
 * run. `target` wins at the start. A change on either side copies to the other at once (`flush: 'sync'`).
 * Towards `target` the Active locale is matched against `supported` (`nl` → `nl-NL`); a locale with no
 * match (`de-DE`) leaves `target` as it is. Call after `setupI18n`; returns the handle that stops it.
 */
export function bridgeLocale(target: Ref<string>, supported: readonly string[]): WatchStopHandle {
    const stopFromTarget = watch(
        target,
        locale => {
            if (locale !== activeLocale.value) setLocale(locale);
        },
        { flush: 'sync', immediate: true },
    );
    const stopToTarget = watch(
        activeLocale,
        locale => {
            const match = locale === undefined ? undefined : matchLocale(locale, supported);
            if (match !== undefined && match !== target.value) target.value = match;
        },
        { flush: 'sync' },
    );

    return () => {
        stopFromTarget();
        stopToTarget();
    };
}
